import { useLazyQuery } from '@apollo/client';
import { IonText } from '@ionic/react';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import AssignmentWeightExplanationModal from '@components/AssignmentWeightExplanationModal';
import ActionIconButton from '@components/buttons/ActionIconButton';
import HandleQuery from '@components/HandleQuery';
import ExpertTablesPage from '@components/pageTypes/ExpertTablesPage';
import Specialties from '@components/Specialties';
import Table, { TableHeader } from '@components/Table';
import TableHeaderExtraInfo from '@components/TableHeaderExtraInfo';
import PageTitle from '@components/text/PageTitle';
import InternalLink from '@components/typography/InternalLink';
import useMyExpert from '@hooks/useMyExpert';
import useNodesFromConnection from '@hooks/useNodesFromConnection';
import useOpener from '@hooks/useOpener';
import {
  ExpertFragment as Expert,
  ExpertSortByEnum,
  GetExpertsDocument,
  GetExpertsQuery,
  GetExpertsQueryVariables,
  SortDirectionEnum
} from '@typing/Generated';
import { expertCoverageTypes } from '@utils/modelUtils';

const ExpertManageExpertsPage = () => {
  const intl = useIntl();
  const myExpert = useMyExpert();
  const assignmentWeightExplanationOpener = useOpener();

  const [getExperts, getExpertsQuery] = useLazyQuery<GetExpertsQuery, GetExpertsQueryVariables>(GetExpertsDocument);

  const [sortBy, setSortBy] = useState<ExpertSortByEnum>(ExpertSortByEnum.client_count);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.descending);

  useEffect(() => {
    getExperts({ variables: { sortBy, sortDirection } });
  }, [getExperts, sortBy, sortDirection]);

  const experts = useNodesFromConnection(getExpertsQuery.data?.experts);

  const expertCoverage = useCallback((expert: Expert) => expertCoverageTypes(expert, intl), [intl]);

  const tableHeaders: TableHeader<ExpertSortByEnum>[] = [
    { columnSortBy: ExpertSortByEnum.name },
    { columnSortBy: ExpertSortByEnum.client_count },
    { messageKey: 'pages.expert.manageExperts.tableHeadings.specialties' },
    {
      columnSortBy: ExpertSortByEnum.journey_assignment_weight,
      extraInfo: (
        <TableHeaderExtraInfo
          descriptionKey="pages.expert.manageExperts.assignmentWeightExplanation"
          linkKey="pages.expert.manageExperts.assignmentWeightLearnMore"
          onOpen={assignmentWeightExplanationOpener.open}
          titleKey="pages.expert.manageExperts.assignmentWeight"
        />
      )
    },
    { messageKey: 'pages.expert.manageExperts.tableHeadings.expert_coverage' },
    { messageKey: 'pages.expert.manageExperts.tableHeadings.actions' }
  ];

  return (
    <ExpertTablesPage helmetKey="pageTitles.expert.expert.list">
      <HandleQuery query={getExpertsQuery}>
        {expertsData => (
          <>
            <PageTitle imageName="womanPlan" titleKey="pages.expert.manageExperts.title" />
            <Table<ExpertSortByEnum>
              fetchMore={getExpertsQuery.fetchMore}
              headerKey="pages.expert.manageExperts.tableHeadings"
              headers={tableHeaders}
              pageInfo={expertsData.experts.pageInfo}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
              sortBy={sortBy}
              sortDirection={sortDirection}
            >
              {experts.map(expert => (
                <tr key={expert.id}>
                  <td>
                    <InternalLink
                      to={
                        expert.id === myExpert?.id ? '/expert/journeys' : `/expert/manage-experts/${expert.id}/journeys`
                      }
                    >
                      {expert.fullName}
                    </InternalLink>
                  </td>
                  <td>
                    <InternalLink
                      to={
                        expert.id === myExpert?.id ? '/expert/journeys' : `/expert/manage-experts/${expert.id}/journeys`
                      }
                    >
                      {expert.memberCount}
                    </InternalLink>
                  </td>
                  <td>
                    <Specialties expert={expert} />
                  </td>
                  <td>
                    <IonText>{expert.journeyAssignmentWeight}</IonText>
                  </td>
                  <td>{expertCoverage(expert)}</td>
                  <td>
                    <ActionIconButton
                      ariaLabelKey="accessibility.ariaLabels.edit"
                      icon="edit"
                      routerLink={`/expert/manage-experts/${expert.id}/edit`}
                    />
                  </td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </HandleQuery>
      <AssignmentWeightExplanationModal opener={assignmentWeightExplanationOpener} />
    </ExpertTablesPage>
  );
};

export default ExpertManageExpertsPage;
